import { useMemo, useState } from 'react';
import { subDays, subMonths } from 'date-fns';
import { DateInputValidationError } from '@cbhq/cds-common/dates/DateInputValidationError';
import { Button } from '@cbhq/cds-web/buttons';
import { Select, SelectOption } from '@cbhq/cds-web/controls';
import { DatePicker } from '@cbhq/cds-web/dates/DatePicker';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';
import { Modal, ModalBody, ModalHeader } from '@cbhq/cds-web/overlays';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { palette } from '@cbhq/cds-web/tokens';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGenerateTransactionsReporting } from ':cloud/queries/OnrampQueries/useGenerateTransactionsReporting';
import { ProductTypeMap, Project } from ':cloud/types/ts_types';

const CUSTOM_PALETTE_NAV_ICON = {
  foreground: 'gray0' as const,
} as const;

const productType = ProductTypeMap.cbpay;

const NINTY_DAYS_AGO = subMonths(new Date(), 3);
const SIXTY_DAYS_AGO = subMonths(new Date(), 2);
const THIRTY_DAYS_AGO = subMonths(new Date(), 1);
const PREVIOUS_DAY = subDays(new Date(), 1);

// replace this with a library
function formatDateToMMDDYY(date: Date | null, delim: string): string {
  if (!date) {
    return '';
  }
  // Get the month, day, and year from the date object
  const month = date.getMonth() + 1; // Months are 0-based, so add 1
  const day = date.getDate();
  const year = date.getFullYear();

  // Pad month and day with leading zeros if necessary
  const formattedMonth = month.toString().padStart(2, '0');
  const formattedDay = day.toString().padStart(2, '0');

  // Format the year to the last two digits
  const formattedYear = year.toString().slice(-2);

  // Return the formatted date string
  return `${formattedMonth}${delim}${formattedDay}${delim}${formattedYear}`;
}

function formatDateToYYYYMMDD(date: Date | null): string {
  if (!date) {
    return '';
  }
  return date.toISOString().split('T')[0];
}

type ReportingProps = {
  project: Project;
};

export function TransactionsReportingSection({ project }: ReportingProps) {
  const { isPhone } = useSimpleBreakpoints();

  /* Create state and callbacks for date range selection */
  const [open, setOpen] = useState(false);
  const [inputStartDate, setInputStartDate] = useState<Date | null>(THIRTY_DAYS_AGO);
  const [inputEndDate, setInputEndDate] = useState<Date | null>(PREVIOUS_DAY);

  const [datePickerStart, setDatePickerStart] = useState<Date | null>(THIRTY_DAYS_AGO);
  const [datePickerEnd, setDatePickerEnd] = useState<Date | null>(PREVIOUS_DAY);

  const [errorStartDate, setErrorStartDate] = useState<DateInputValidationError | null>(null);
  const [errorEndDate, setErrorEndDate] = useState<DateInputValidationError | null>(null);

  const [dropdownValue, setDropdownValue] = useState('Last 30 days');

  const DROPDOWN_OPTIONS = useMemo(
    () => [
      {
        tile: 'Last 30 days',
        value: 'Last 30 days',
        callback: () => {
          setInputStartDate(THIRTY_DAYS_AGO);
          setInputEndDate(PREVIOUS_DAY);
          setDatePickerStart(THIRTY_DAYS_AGO);
          setDatePickerEnd(PREVIOUS_DAY);
        },
      },
      {
        tile: 'Last 60 days',
        value: 'Last 60 days',
        callback: () => {
          setInputStartDate(SIXTY_DAYS_AGO);
          setInputEndDate(PREVIOUS_DAY);
          setDatePickerStart(SIXTY_DAYS_AGO);
          setDatePickerEnd(PREVIOUS_DAY);
        },
      },
      {
        tile: 'Last 90 days',
        value: 'Last 90 days',
        callback: () => {
          setInputStartDate(NINTY_DAYS_AGO);
          setInputEndDate(PREVIOUS_DAY);
          setDatePickerStart(NINTY_DAYS_AGO);
          setDatePickerEnd(PREVIOUS_DAY);
        },
      },
      {
        tile: 'Custom date range',
        value: `${formatDateToMMDDYY(inputStartDate, '/')} - ${formatDateToMMDDYY(
          inputEndDate,
          '/',
        )}`,
        callback: () => {
          setOpen(true);
        },
      },
    ],
    [inputStartDate, inputEndDate],
  );

  /* Create callbacks to open & close the modal */
  const handleOnRequestClose: () => void = () => {
    // if we close the modal without saving, reset the date picker values to current input dates
    setDatePickerStart(inputStartDate);
    setDatePickerEnd(inputEndDate);
    setErrorEndDate(null);
    setOpen(false);
  };

  const handleOnRequestApply: () => void = () => {
    // if we apply the custom dates, then update the input dates to the current date picker values
    // don't allow applying if either date picker is null. Return early here.
    if (datePickerStart === null || datePickerEnd === null) {
      setErrorStartDate(new DateInputValidationError('custom', 'Please enter a date'));
      setErrorEndDate(new DateInputValidationError('custom', 'Please enter a date'));
      return;
    }

    setInputStartDate(datePickerStart);
    setInputEndDate(datePickerEnd);
    setErrorEndDate(null);

    setOpen(false);
    setDropdownValue(
      `${formatDateToMMDDYY(datePickerStart, '/')} - ${formatDateToMMDDYY(datePickerEnd, '/')}`,
    );
  };

  const updateDate = (startDate: Date | null, endDate: Date | null) => {
    if (!startDate) {
      return;
    }

    if (!endDate) {
      setDatePickerStart(startDate);
      setErrorStartDate(null);
      setErrorEndDate(null);
      return;
    }

    if (startDate > endDate) {
      setErrorEndDate(new DateInputValidationError('custom', 'Must come before start date'));
    } else {
      setDatePickerStart(startDate);
      setDatePickerEnd(endDate);
      setErrorEndDate(null);
    }
  };

  /* Create state and callbacks for report generation + polling status */
  const { isLoading, startReportGeneration: handleGenerateTransactionsReport } =
    useGenerateTransactionsReporting({
      project,
      productType,
      startDate: formatDateToYYYYMMDD(inputStartDate),
      endDate: formatDateToYYYYMMDD(inputEndDate),
    });

  return (
    <VStack gap={4}>
      <VStack bordered borderRadius="rounded" spacing={4} bottom={10}>
        <Modal visible={open} onRequestClose={handleOnRequestClose} width={560}>
          <ModalHeader title="Select a custom date range" />
          <ModalBody>
            <HStack gap={2} spacing={2} alignItems="center" spacingBottom={8}>
              <DatePicker
                label="From"
                date={datePickerStart}
                onChangeDate={(date) => updateDate(date, datePickerEnd)}
                error={errorStartDate}
                onErrorDate={setErrorStartDate}
                maxDate={PREVIOUS_DAY}
                disabled={isLoading}
                disabledDateError="Date unavailable"
              />
              <p> — </p>
              <DatePicker
                label="To"
                date={datePickerEnd}
                onChangeDate={(date) => updateDate(datePickerStart, date)}
                error={errorEndDate}
                onErrorDate={setErrorEndDate}
                maxDate={PREVIOUS_DAY}
                disabled={isLoading || !datePickerStart}
                disabledDateError="Date unavailable"
              />
            </HStack>
            <HStack justifyContent="flex-end" gap={2}>
              <Button onPress={handleOnRequestClose} variant="secondary">
                Cancel
              </Button>
              <Button onPress={handleOnRequestApply}>Apply</Button>
            </HStack>
          </ModalBody>
        </Modal>

        <HStack alignItems="center" gap={2}>
          {!isPhone && (
            <Box
              dangerouslySetBackground={palette.foreground}
              height={24}
              width={24}
              borderRadius="roundedFull"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ThemeProvider palette={CUSTOM_PALETTE_NAV_ICON}>
                <Icon name="protection" size="s" color="primaryForeground" />
              </ThemeProvider>
            </Box>
          )}
          <VStack gap={2} flexGrow={1}>
            <VStack>
              <TextHeadline as="h1">Generate transactions report</TextHeadline>
              <TextBody as="p" color="foregroundMuted">
                Download a CSV file of all Onramp transactions for this project.
              </TextBody>
            </VStack>
          </VStack>
          <Select
            value={dropdownValue}
            onChange={setDropdownValue}
            width={200}
            disabled={isLoading}
          >
            {DROPDOWN_OPTIONS.map(({ tile, value, callback }) => (
              <SelectOption key={tile} value={value} title={tile} onPress={callback} />
            ))}
          </Select>
          {!isLoading ? (
            <Button onPress={handleGenerateTransactionsReport}>Download CSV</Button>
          ) : (
            <Spinner size={2} color="primary" />
          )}
          <Box spacingEnd={5} />
        </HStack>
      </VStack>
    </VStack>
  );
}
